import React, { useState, useEffect } from 'react';
import { 
  TextField, 
  MenuItem, 
  Box, 
  Typography, 
  DialogActions, 
  Button,
  Select,
  FormControl
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { 
  StyledPreviewDialog, 
  PreviewHeader, 
  PreviewContent, 
  FormGrid, 
  PreviewFieldWrapper, 
  PreviewLabel,
  FileUploadPreview 
} from './styles';
import { SignupField } from '../../../../../../modules/superadmin/types';

interface PreviewModalProps {
  open: boolean;
  onClose: () => void;
  fields: SignupField[];
}

const PreviewModal: React.FC<PreviewModalProps> = ({ open, onClose, fields }) => {
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // Reset form values when modal closes
  useEffect(() => {
    if (!open) {
      setFormValues({});
    }
  }, [open]);

  // Filter active fields and sort by order
  const sortedFields = fields
    .filter(field => field.is_active)
    .sort((a, b) => a.order - b.order);

  const renderField = (field: SignupField) => {
    const fieldValue = formValues[field.field_id] || '';
    
    const handleChange = (e: any) => {
      setFormValues(prev => ({
        ...prev,
        [field.field_id]: e.target.value
      }));
    };

    switch (field.field_type) {
      case 'select':
        const options = field.options ? field.options.split(',').map(o => o.trim()) : [];
        return (
          <FormControl fullWidth size="small">
            <Select 
              value={fieldValue} 
              onChange={handleChange}
              displayEmpty
            >
              <MenuItem value="" disabled>Select {field.label}</MenuItem>
              {options.map((opt, idx) => (
                <MenuItem key={idx} value={opt}>{opt}</MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'date':
        return (
          <TextField
            type="date"
            fullWidth
            size="small"
            value={fieldValue}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'file':
        return (
          <FileUploadPreview>
            <CloudUploadIcon sx={{ fontSize: 32, mb: 1, color: '#5E8E7E' }} />
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              Upload {field.label}
            </Typography>
            <Typography variant="caption" sx={{ color: '#999' }}>
              Mapped to: {field.document_type_name || 'N/A'}
            </Typography>
          </FileUploadPreview>
        );

      case 'textarea':
        return (
          <TextField
            placeholder={`Enter ${field.label}`}
            fullWidth
            multiline
            rows={3}
            size="small"
            value={fieldValue}
            onChange={handleChange}
          />
        );

      case 'number':
        return (
          <TextField
            type="number"
            placeholder={`Enter ${field.label}`}
            fullWidth
            size="small"
            value={fieldValue}
            onChange={handleChange}
          />
        );

      case 'password':
        return (
          <TextField
            type="password"
            placeholder={`Enter ${field.label}`}
            fullWidth
            size="small"
            value={fieldValue}
            onChange={handleChange}
          />
        );

      default:
        return (
          <TextField
            placeholder={`Enter ${field.label}`}
            fullWidth
            size="small"
            value={fieldValue}
            onChange={handleChange}
          />
        );
    }
  };

  return (
    <StyledPreviewDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <PreviewHeader>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#333' }}>
          Organization Signup Form Preview
        </Typography>
        <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
          This is how the form will appear to organizations during registration.
        </Typography>
      </PreviewHeader>

      <PreviewContent dividers>
        <FormGrid>
          {sortedFields.map((field) => (
            <PreviewFieldWrapper 
              key={field.field_id} 
              sx={{ gridColumn: field.field_type === 'textarea' || field.field_type === 'file' ? 'span 2' : 'span 1' }}
            >
              <PreviewLabel>
                {field.label} {field.is_required && <Box component="span" sx={{ color: 'red' }}>*</Box>}
              </PreviewLabel>
              {renderField(field)}
            </PreviewFieldWrapper>
          ))}
        </FormGrid>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Button 
            variant="contained" 
            sx={{ 
              backgroundColor: '#5E8E7E', 
              px: 6, 
              py: 1.5, 
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: '8px',
              '&:hover': { backgroundColor: '#4A7A6A' }
            }}
          >
            Submit Registration
          </Button>
        </Box>
      </PreviewContent>

      <DialogActions sx={{ p: 2, backgroundColor: '#fff' }}>
        <Button onClick={onClose} sx={{ color: '#666', fontWeight: 600 }}>Close Preview</Button>
      </DialogActions>
    </StyledPreviewDialog>
  );
};

export default PreviewModal;
